import { useQuery, useQueries } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { useIsCmsEnabled } from "src/api/client-api/cms/useIsCmsEnabled";
import { zodParseWithDetailedLog } from "src/utils/zodErrorLogger";
import { z } from "zod";

// Types for PuckExtension
export const PuckExtensionSchema = z.object({
    id: z.number(),
    name: z.string(),
    extensionPoint: z.string(),
    puckData: z.string().nullable(),
    conditions: z.any(),
    priority: z.number(),
    status: z.enum(["active", "inactive", "draft"]),
    createdAt: z.string(),
    updatedAt: z.string(),
    allowedRoles: z.array(z.string()).optional(),
    sectors: z.array(z.string()).optional(),
    createdBy: z.string().nullable().optional(),
    canView: z.boolean().optional(),
    canModify: z.boolean().optional(),
    canDelete: z.boolean().optional(),
});

export const PuckExtensionResponseSchema = z.object({
    "hydra:member": z.array(PuckExtensionSchema),
    "hydra:totalItems": z.number(),
});

export const RuntimePuckExtensionSchema = PuckExtensionSchema.pick({
    id: true,
    name: true,
    extensionPoint: true,
    puckData: true,
    conditions: true,
    priority: true,
    status: true,
    canView: true,
});

export const RuntimePuckExtensionResponseSchema = z.object({
    "hydra:member": z.array(RuntimePuckExtensionSchema),
    "hydra:totalItems": z.number(),
});

export const PuckExtensionManagementOptionsSchema = z.object({
    roles: z.array(
        z.object({
            code: z.string(),
            name: z.string(),
        }),
    ),
    sectors: z.array(
        z.object({
            iri: z.string(),
            name: z.string(),
        }),
    ),
});

export type PuckExtension = z.infer<typeof PuckExtensionSchema>;
export type PuckExtensionResponse = z.infer<typeof PuckExtensionResponseSchema>;
export type RuntimePuckExtension = z.infer<typeof RuntimePuckExtensionSchema>;
export type PuckExtensionManagementOptions = z.infer<typeof PuckExtensionManagementOptionsSchema>;
export type PuckExtensionSectorOption = PuckExtensionManagementOptions["sectors"][number];

/**
 * Clés de cache pour les extensions
 */
export const EXTENSION_QUERY_KEYS = {
    EXTENSIONS: "puck-extensions",
    EXTENSION: "puck-extension",
    BY_POINT: "extensions-by-point",
    BY_PAGE: "extensions-by-page",
    STATISTICS: "extension-statistics",
    MANAGEMENT_OPTIONS: "puck-extension-management-options",
} as const;

export const usePuckExtensionManagementOptions = () => {
    const isCmsEnabled = useIsCmsEnabled();

    return useQuery({
        queryKey: [EXTENSION_QUERY_KEYS.MANAGEMENT_OPTIONS],
        queryFn: async () => {
            const response = await loggedInApiCall.get("/api/puck_extensions/management/options");

            return zodParseWithDetailedLog(response.data, PuckExtensionManagementOptionsSchema, {
                operation: "usePuckExtensionManagementOptions",
                endpoint: "puck-extension-management-options",
            });
        },
        enabled: isCmsEnabled,
        staleTime: 5 * 60 * 1000,
    });
};

export const usePuckExtension = (extensionId: number, options: BaseExtensionOptions = {}) => {
    const { enabled = true, cacheTime = 2 * 60 * 1000, staleTime = 30 * 1000 } = options;
    const isCmsEnabled = useIsCmsEnabled();

    return useQuery({
        queryKey: [EXTENSION_QUERY_KEYS.EXTENSION, extensionId],
        queryFn: async () => {
            const response = await loggedInApiCall.get(`/api/puck_extensions/${extensionId}`);

            return zodParseWithDetailedLog(response.data, PuckExtensionSchema, {
                operation: "usePuckExtension",
                endpoint: `extension-${extensionId}`,
            });
        },
        enabled: enabled && extensionId > 0 && isCmsEnabled,
        cacheTime,
        staleTime,
    });
};

/**
 * Options pour les hooks d'extensions
 */
type BaseExtensionOptions = {
    enabled?: boolean;
    cacheTime?: number;
    staleTime?: number;
};

/**
 * Contexte pour l'évaluation des conditions
 */
export type ExtensionContext = {
    userRoles?: string[];
    deviceType?: "mobile" | "desktop" | "tablet";
    featureFlags?: Record<string, boolean>;
    userId?: number;
    companyId?: number;
    [key: string]: any;
};

type ExtensionBatchExtras = {
    userRoles?: string[];
    deviceType?: ExtensionContext["deviceType"];
    featureFlags?: Record<string, boolean>;
};

type PendingExtensionRequest = {
    extensionPoint: string;
    extras: ExtensionBatchExtras;
    resolve: (extensions: RuntimePuckExtension[]) => void;
    reject: (error: unknown) => void;
};

let pendingExtensionRequests: PendingExtensionRequest[] = [];
let extensionBatchFlushScheduled = false;

const serializeExtensionBatchExtras = (extras: ExtensionBatchExtras): string =>
    JSON.stringify({
        userRoles: extras.userRoles ? [...extras.userRoles].sort() : undefined,
        deviceType: extras.deviceType,
        featureFlags: extras.featureFlags,
    });

const fetchExtensionBatch = async (group: PendingExtensionRequest[], allowFallback = true): Promise<void> => {
    const uniqueExtensionPoints = Array.from(new Set(group.map((request) => request.extensionPoint)));
    const queryParams = new URLSearchParams();
    uniqueExtensionPoints.forEach((extensionPoint) => queryParams.append("extensionPoint[]", extensionPoint));

    const { userRoles, deviceType, featureFlags } = group[0].extras;
    if (userRoles) queryParams.append("user_roles", userRoles.join(","));
    if (deviceType) queryParams.append("device_type", deviceType);
    if (featureFlags) queryParams.append("feature_flags", JSON.stringify(featureFlags));

    try {
        const response = await loggedInApiCall.get(`/api/puck_extensions?${queryParams.toString()}`, {
            headers: {
                Accept: "application/ld+json",
            },
        });

        const validatedData = zodParseWithDetailedLog(response.data, RuntimePuckExtensionResponseSchema, {
            operation: "useExtensions",
            endpoint: `extensions-batch-${uniqueExtensionPoints.join(",")}`,
        });

        const extensionsByPoint = new Map<string, RuntimePuckExtension[]>();
        (validatedData["hydra:member"] || []).forEach((extension) => {
            const existing = extensionsByPoint.get(extension.extensionPoint);
            if (existing) {
                existing.push(extension);
            } else {
                extensionsByPoint.set(extension.extensionPoint, [extension]);
            }
        });

        group.forEach((request) => request.resolve(extensionsByPoint.get(request.extensionPoint) ?? []));
    } catch (error) {
        if (!allowFallback || uniqueExtensionPoints.length === 1) {
            group.forEach((request) => request.reject(error));
            return;
        }

        // A merged request can fail for reasons specific to a single extension point (e.g. one
        // record failing schema validation). Re-issue one request per point so an unrelated CMS
        // zone isn't dragged into an error state just because it happened to batch with it.
        await Promise.all(
            uniqueExtensionPoints.map((extensionPoint) =>
                fetchExtensionBatch(
                    group.filter((request) => request.extensionPoint === extensionPoint),
                    false,
                ),
            ),
        );
    }
};

const flushExtensionBatch = (): void => {
    const requests = pendingExtensionRequests;
    pendingExtensionRequests = [];

    const groups = new Map<string, PendingExtensionRequest[]>();
    requests.forEach((request) => {
        const key = serializeExtensionBatchExtras(request.extras);
        const group = groups.get(key);
        if (group) {
            group.push(request);
        } else {
            groups.set(key, [request]);
        }
    });

    groups.forEach((group) => {
        void fetchExtensionBatch(group);
    });
};

const scheduleExtensionBatchFlush = (): void => {
    if (extensionBatchFlushScheduled) return;
    extensionBatchFlushScheduled = true;
    queueMicrotask(() => {
        extensionBatchFlushScheduled = false;
        flushExtensionBatch();
    });
};

const requestExtensionsBatched = (
    extensionPoint: string,
    extras: ExtensionBatchExtras,
): Promise<RuntimePuckExtension[]> =>
    new Promise((resolve, reject) => {
        pendingExtensionRequests.push({ extensionPoint, extras, resolve, reject });
        scheduleExtensionBatchFlush();
    });

/**
 * Hook pour récupérer les extensions pour un extension point spécifique.
 *
 * Les appels concurrents (plusieurs `ExtensionPoint` montés dans le même tick) sont
 * regroupés en une seule requête HTTP `extensionPoint[]=...` pour éviter de rejouer
 * le coût d'authentification/autorisation une fois par zone affichée. En cas d'échec
 * d'une requête groupée, chaque point est rejoué individuellement pour isoler les
 * pannes propres à une seule zone.
 */
export const useExtensions = (
    extensionPoint: string,
    context?: ExtensionContext,
    options: BaseExtensionOptions = {},
) => {
    const { enabled = true, cacheTime = 5 * 60 * 1000, staleTime = 60 * 1000 } = options;
    const isCmsEnabled = useIsCmsEnabled();

    const query = useQuery({
        queryKey: [EXTENSION_QUERY_KEYS.BY_POINT, extensionPoint, context],
        queryFn: () =>
            requestExtensionsBatched(extensionPoint, {
                userRoles: context?.userRoles,
                deviceType: context?.deviceType,
                featureFlags: context?.featureFlags,
            }),
        enabled: enabled && !!extensionPoint && isCmsEnabled,
        cacheTime,
        staleTime,
        retry: (failureCount, error: any) => {
            // Ne pas retry sur les erreurs 4xx
            if (error?.response?.status && error.response.status < 500) {
                return false;
            }
            return failureCount < 2;
        },
        onError: (error) => {
            console.error(`Failed to fetch extensions for point "${extensionPoint}":`, error);
        },
    });

    return {
        extensions: query.data ?? [],
        ...query,
    };
};

/**
 * Hook optimisé pour récupérer les extensions de plusieurs points en une seule requête (batch loading)
 */
export const usePageExtensions = (pageType: string, context?: ExtensionContext, options: BaseExtensionOptions = {}) => {
    const { enabled = true, cacheTime = 5 * 60 * 1000, staleTime = 60 * 1000 } = options;
    const isCmsEnabled = useIsCmsEnabled();

    const query = useQuery({
        queryKey: [EXTENSION_QUERY_KEYS.BY_PAGE, pageType, context],
        queryFn: async () => {
            const response = await loggedInApiCall.get(`/api/puck_extensions/page/${encodeURIComponent(pageType)}`);
            return zodParseWithDetailedLog(response.data, z.record(z.string(), z.array(PuckExtensionSchema)), {
                operation: "usePageExtensions",
                endpoint: `page-extensions-${pageType}`,
            });
        },
        enabled: enabled && !!pageType && isCmsEnabled,
        cacheTime,
        staleTime,
        retry: (failureCount, error: any) => {
            if (error?.response?.status && error.response.status < 500) {
                return false;
            }
            return failureCount < 2;
        },
        onError: (error) => {
            console.error(`Failed to fetch page extensions for "${pageType}":`, error);
        },
    });

    return {
        extensionsByPoint: query.data ?? {},
        getExtensions: (extensionPoint: string): PuckExtension[] => {
            return (query.data?.[extensionPoint] ?? []) as PuckExtension[];
        },
        ...query,
    };
};

/**
 * Hook pour récupérer plusieurs extension points en parallèle (pour les cas où batch loading n'est pas possible)
 */
export const useMultipleExtensions = (
    extensionPoints: string[],
    context?: ExtensionContext,
    options: BaseExtensionOptions = {},
) => {
    const { enabled = true, cacheTime = 5 * 60 * 1000, staleTime = 60 * 1000 } = options;
    const isCmsEnabled = useIsCmsEnabled();

    const queries = useQueries(
        extensionPoints.map((extensionPoint) => ({
            queryKey: [EXTENSION_QUERY_KEYS.BY_POINT, extensionPoint, context],
            queryFn: async () => {
                const queryParams = new URLSearchParams();
                if (context?.userRoles) {
                    queryParams.append("user_roles", context.userRoles.join(","));
                }
                if (context?.deviceType) {
                    queryParams.append("device_type", context.deviceType);
                }
                if (context?.featureFlags) {
                    queryParams.append("feature_flags", JSON.stringify(context.featureFlags));
                }

                const queryString = queryParams.toString();
                const url = `/api/puck_extensions/point/${encodeURIComponent(extensionPoint)}${queryString ? `?${queryString}` : ""}`;

                const response = await loggedInApiCall.get(url);
                return zodParseWithDetailedLog(response.data, z.array(PuckExtensionSchema), {
                    operation: "useMultipleExtensions",
                    endpoint: `extensions-${extensionPoint}`,
                });
            },
            enabled: enabled && !!extensionPoint && isCmsEnabled,
            cacheTime,
            staleTime,
            retry: (failureCount: number, error: any) => {
                if (error?.response?.status && error.response.status < 500) {
                    return false;
                }
                return failureCount < 2;
            },
        })),
    );

    // Transformer le résultat en objet indexé par extension point
    const extensionsByPoint: Record<string, PuckExtension[]> = {};
    extensionPoints.forEach((point, index) => {
        extensionsByPoint[point] = queries[index]?.data ?? [];
    });

    const isLoading = queries.some((q) => q.isLoading);
    const isError = queries.some((q) => q.isError);
    const isFetching = queries.some((q) => q.isFetching);

    return {
        extensionsByPoint,
        getExtensions: (extensionPoint: string): PuckExtension[] => {
            return extensionsByPoint[extensionPoint] ?? [];
        },
        queries,
        isLoading,
        isError,
        isFetching,
    };
};

/**
 * Hook pour vérifier si des extensions existent pour un point donné (optimisation)
 */
export const useHasExtensions = (extensionPoint: string, options: BaseExtensionOptions = {}) => {
    const { enabled = true, cacheTime = 10 * 60 * 1000, staleTime = 5 * 60 * 1000 } = options;
    const isCmsEnabled = useIsCmsEnabled();

    const query = useQuery({
        queryKey: [EXTENSION_QUERY_KEYS.BY_POINT, "exists", extensionPoint],
        queryFn: async () => {
            const response = await loggedInApiCall.head(
                `/api/puck_extensions/point/${encodeURIComponent(extensionPoint)}`,
            );
            return response.headers["x-total-count"] ? parseInt(response.headers["x-total-count"]) > 0 : false;
        },
        enabled: enabled && !!extensionPoint && isCmsEnabled,
        cacheTime,
        staleTime,
        retry: false, // Pas besoin de retry pour une vérification d'existence
    });

    return {
        hasExtensions: query.data ?? false,
        ...query,
    };
};

/**
 * Hook pour récupérer toutes les extensions (admin/gestion)
 */
export const useAllExtensions = (
    filters?: {
        extensionPoint?: string;
        status?: string;
        search?: string;
        page?: number;
        limit?: number;
    },
    options: BaseExtensionOptions = {},
) => {
    const { enabled = true, cacheTime = 2 * 60 * 1000, staleTime = 30 * 1000 } = options;
    const isCmsEnabled = useIsCmsEnabled();

    const queryParams = new URLSearchParams();
    if (filters?.extensionPoint) queryParams.append("extensionPoint", filters.extensionPoint);
    if (filters?.status) queryParams.append("status", filters.status);
    if (filters?.search) queryParams.append("search", filters.search);
    if (filters?.page) queryParams.append("page", filters.page.toString());
    if (filters?.limit) queryParams.append("itemsPerPage", filters.limit.toString());

    const queryString = queryParams.toString();
    const url = `/api/puck_extensions/management${queryString ? `?${queryString}` : ""}`;

    const query = useQuery({
        queryKey: [EXTENSION_QUERY_KEYS.EXTENSIONS, filters],
        queryFn: async () => {
            const response = await loggedInApiCall.get(url);
            return zodParseWithDetailedLog(response.data, PuckExtensionResponseSchema, {
                operation: "useAllExtensions",
                endpoint: "all-extensions",
            });
        },
        enabled: enabled && isCmsEnabled,
        cacheTime,
        staleTime,
        keepPreviousData: true, // Garde les données précédentes pendant le chargement des nouvelles
    });

    return {
        extensions: query.data?.["hydra:member"] ?? [],
        totalItems: query.data?.["hydra:totalItems"] ?? 0,
        ...query,
    };
};
